import { SearchX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { careersPath } from '@/modules/careers/careers';
import { CareersJobView } from '@/modules/careers/components/careersJobView/CareersJobView';
import { CareersLayout } from '@/modules/careers/components/careersLayout/CareersLayout';
import { CareersPageView } from '@/modules/careers/components/careersPageView/CareersPageView';
import { useCareersPageQuery } from '@/modules/careers/queries/useCareersPageQuery';
import { PortalEmptyState } from '@/modules/hiring-manager/components/portalEmptyState/PortalEmptyState';
import { PortalListSkeleton } from '@/modules/hiring-manager/components/PortalList';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { errorHint } from '@/utils/actionErrorMessage';

/**
 * A company's public Careers Page at `/:companySlug/careers`, and one job's
 * page at `/:companySlug/careers/:jobId`. Both read the same response, so
 * opening a job from the list needs no second request.
 */
export function CareersPage() {
  const { companySlug = '', jobId } = useParams<{
    companySlug: string;
    jobId?: string;
  }>();
  const careersPageQuery = useCareersPageQuery(companySlug);

  // The page rises in only where it replaces the skeleton. Moving between the
  // list and a job reuses this instance and the cached page, which should just be there.
  const [enter, setEnter] = useState({
    jobId,
    animate: careersPageQuery.isPending,
  });
  if (enter.jobId !== jobId) setEnter({ jobId, animate: false });

  // The router does not restore scroll, so a job opened from far down the list would start mid-page.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [jobId]);

  if (careersPageQuery.isPending) {
    return (
      <CareersLayout title="Careers">
        <div className="flex flex-col gap-4 pt-8 pb-10 md:pt-16">
          <Skeleton className="h-12 w-full max-w-md bg-neutral-200" />
          <Skeleton className="h-4 w-72 bg-neutral-200" />
        </div>
        <PortalListSkeleton rows={3} />
      </CareersLayout>
    );
  }

  if (!careersPageQuery.data) {
    const { error } = careersPageQuery;
    const isNotFound =
      error !== null && 'status' in error && error.status === 404;

    return (
      <CareersLayout title="Careers">
        <PortalEmptyState
          description={
            isNotFound
              ? 'The link may be mistyped, or the company no longer lists its roles here.'
              : errorHint(error)
          }
          icon={SearchX}
          title={
            isNotFound
              ? 'This careers page isn’t available'
              : 'We couldn’t load this careers page'
          }
        />
      </CareersLayout>
    );
  }

  const page = careersPageQuery.data;
  if (!jobId) {
    return (
      <CareersPageView
        animateEnter={enter.animate}
        companySlug={companySlug}
        page={page}
      />
    );
  }

  const job = page.jobs.find(({ id }) => id === jobId);
  if (!job) {
    return (
      <CareersLayout
        company={{ name: page.companyName, slug: companySlug }}
        title={`Careers at ${page.companyName}`}
      >
        <PortalEmptyState
          action={{ label: 'See open roles', to: careersPath(companySlug) }}
          description="It may have closed, or the link may be mistyped."
          icon={SearchX}
          title="This role isn’t open anymore"
        />
      </CareersLayout>
    );
  }

  return (
    <CareersJobView
      animateEnter={enter.animate}
      companySlug={companySlug}
      job={job}
      page={page}
    />
  );
}
